<template>
  <div class="machining-option__variable" :class="{'col-xs-6': !hasInputAndSelect, 'col-xs-12': hasInputAndSelect}">
    <label :for="data.varName">{{data.displayName}} ({{data.varName}}) <span v-if="data.helpHMTL" class="help-icon glyphicon glyphicon-info-sign" @click.stop="helpIconClicked"></span></label>

    <div class="radio-and-input-container" :class="{'row': hasInputAndSelect}">
      <div v-if="hasInputAndSelect" class="radio-container" :class="{'col-xs-6': hasInputAndSelect}">
        <div class="radio">
          <label><input type="radio" name="selectOrInput" value="select" required v-model="selectOrInput" @change="selectOrInputRadioHasChanged">{{rangeForSelect}}</label>
        </div>
        <div class="radio">
          <label><input type="radio" name="selectOrInput" value="input" required v-model="selectOrInput" @change="selectOrInputRadioHasChanged">{{range}}</label>
        </div>
      </div>

      <div class="input-container" :class="{'col-xs-6': hasInputAndSelect}">
        <select v-if="optionType == 'select'" :ref="data.varName" :id="data.varName" :name="data.varName" required v-model="selectValue" @change="valueHasChanged">
          <option disabled hidden value="">{{$t('select')}}</option>
          <option v-for="option in data.options" :key="option.code" :value="option.code">{{option.description}}</option>
        </select>
        <template v-else-if="optionType == 'input'">
          <input v-if="!data.readOnly" type="number" :ref="data.varName" :id="data.varName" :name="data.varName" :min="data.min" :max="data.max" :step="precision" :required="!data.readOnly" :disabled="data.readOnly" :class="{'has-error': inputEnteredIsInvalid}" v-model.number="inputValue" @change="valueHasChanged">
          <input v-else type="text" :ref="data.varName" :id="data.varName" :name="data.varName" :disabled="true" v-model="inputValueReadOnly">
          <div class="error" v-show="inputEnteredIsInvalid">{{ inputErrorMessage }}</div>
        </template>

        <div>
          <span v-if="range && optionType == 'input'" class="input-tag">{{range}}</span>
          <span v-if="optionType && tolerances" class="input-tag tolerance-tag info-tooltip">{{$t('tolerance')}}
            <div class="info-tooltiptext">
              <div v-for="(toleranceObject, index) in tolerances" :key="'tolerance' + index">{{toleranceObject.tolerance}}</div>
            </div>
          </span>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'MachiningOptionVariable',
  props: {
    data: Object,
  },
  data: () => {
    return {
      publicPath: process.env.BASE_URL,

      selectValue: "", // Initialize as empty string in order to show "Select" placeholder by default
      inputValue: null,

      selectOrInput: null,

      inputEnteredIsInvalid: null,
      inputErrorMessage: null,
    }
  },
  mounted() {
    if (this.hasInputAndSelect && this.data.value != null) {
      // For a configured partnumber, we need to know whether the Coaxial Hole variable "Hole Diameter (AH1)" value came from the dropdown or the input box.
      // The correct radio option needs to be selected, and only the select or the input should be populated, depending on where the value came from
      // All dropdown values begin with "DR"
      if (this.data.value.startsWith('DR')) {
        this.selectOrInput = 'select';
        this.selectValue = this.data.value;
      } else {
        this.selectOrInput = 'input';
        this.inputValue = this.data.decimalValue;
      }
    } else {
      this.selectValue = this.data.value || ""; // Initialize as empty string in order to show "Select" placeholder by default
      this.inputValue = this.data.decimalValue;
    }
  },
  computed: {
    // Coaxial Hole variable "Hole Diameter (AH1)" is a special case that has a dropdown list of options up to a certain diameter, and above that it has an input box.
    // We determine that this option should have both a select and an input by the fact that data.isDropDown is true, and that it also has a min and max range.
    // (Normally, a dropdown does not have a range min and max)
    // The dropdown should be used for values up to the min, and between the min and max the input should be used.
    hasInputAndSelect() { return this.data.isDropDown && (this.data.min || this.data.max)},

    optionType() { return this.hasInputAndSelect ? this.selectOrInput : this.data.isDropDown ? "select" : "input" },

    precision() {
      return +(.1 ** this.data.precision).toFixed(this.data.precision);
    },

    range() {
      if (this.data.min != null && this.data.max != null) {
        return this.$t('machinined-option-range', {min: this.data.min, max: this.data.max, unit: this.unitTranslated(this.data.uom)})
      }
    },
    tolerances() {
      let tolerances = null;

      if (this.data.tolerances.length) {
        if (this.optionType == 'select') {
          let selectedOption = this.data.options.find(x => x.code == this.selectValue);
          if (selectedOption) {
            tolerances = this.data.tolerances.filter(x => (x.minVal == null || x.minVal <= selectedOption.value) && (x.maxVal == null || x.maxVal >= selectedOption.value));
          }
        } else {
          tolerances = this.data.tolerances.filter(x => (x.minVal == null || x.minVal <= this.inputValue) && (x.maxVal == null || x.maxVal >= this.inputValue));
        }
      }

      return tolerances && tolerances.length ? tolerances : null;
    },

    rangeForSelect() {
      if (this.data.min) {
        return this.$t('machinined-option-range-less-than-min', {min: this.data.min, unit: this.unitTranslated(this.data.uom)})
      }
    },

    inputValueReadOnly() { return this.inputValue ? `${this.inputValue} ${this.data.uom}` : '' },

  },
  methods: {
    unitTranslated(unit) {
      switch (unit) {
        case 'mm':
          return this.$t('millimeter-short');
        case 'in':
          return this.$t('inch-short');
        default:
          return '';
      }
    },

    selectOrInputRadioHasChanged() {
      if (this.checkValidity()) {
        this.$emit('valueHasChanged', true);
      } else {
        this.$emit('valueHasChanged', false);
      }
    },

    valueHasChanged() {
      this.$emit('valueHasChanged', this.checkValidity());
    },

    checkValidity() {
      if (this.optionType == 'select' && this.selectValue != null && this.selectValue !== '') {
        // This is a select variable and a value has been selected
        return true;
      } else if (this.optionType == 'input' && this.inputValue != null && this.inputValue !== '') {

        // This is an input variable and a value has been entered
        if (this.inputValue < this.data.min || this.inputValue > this.data.max) {
          // The entered value is outside the min/max range
          this.inputEnteredIsInvalid = true;
          this.inputErrorMessage = this.$t('error.value-must-be-between-min-and-max', [this.data.min, this.data.max]);
          return false;
        } else if (this.data.precision != null && this.inputValue.countDecimals() > this.data.precision) {
          // The entered value has too many decimal places
          this.inputEnteredIsInvalid = true;
          this.inputErrorMessage = this.$t('error.value-must-have-less-decimals-than-precision', [this.data.precision]);
          return false;
        } else {
          this.inputEnteredIsInvalid = false;
          return true;
        }
        
      } else {
        // A value has not been entered
        return false;
      }
    },

    helpIconClicked() {
      this.$emit('helpIconClicked', this.data.varName);
    },


    // #region These functions are called from the parent component
    updateVariableValue() {
      if (!this.data.readOnly) {
        if ((this.hasInputAndSelect && this.selectOrInput == 'select') || (!this.hasInputAndSelect && this.data.isDropDown)) {
          this.data.value = this.selectValue;
        } else {
          this.data.value = this.inputValue.toFixed(this.data.precision).replace('.', '').padStart(this.data.size, '0');
        }
      }
    },
    // #endregion
  }
}

// https://stackoverflow.com/a/17369245
Number.prototype.countDecimals = function () {

  if (Math.floor(this.valueOf()) === this.valueOf()) return 0;

  var str = this.toString();
  if (str.indexOf(".") !== -1 && str.indexOf("-") !== -1) {
    return str.split("-")[1] || 0;
  } else if (str.indexOf(".") !== -1) {
    return str.split(".")[1].length || 0;
  }
  return str.split("-")[1] || 0;
}
</script>

<style scoped lang="scss">
.thomson-app {
  .machining-option__variable {
    margin-bottom: 5px;
  }

  label {
    display: block;
    color: #666766;
    font-weight: normal; // Added to override Bootstrap on TLO
    margin-bottom: 0; // Added to override Bootstrap on TLO
  }

  .help-icon {
    top: 2px;
    margin-left: 2px;
  }

  .radio {
    margin: 5px 0;
  }
  .radio label {
    font-weight: bold;
    cursor: pointer;
  }
  input[type=radio] {
    position: static; /* Added to fix TLO CSS */
    vertical-align: middle;
    cursor: pointer;
    margin: 0 5px 0 0;
    height: 16px;
    width: 16px;
  }
  
  input[type=text],
  input[type=number],
  select {
    display: block;
    width: 100%;
    height: 34px;
    padding: 6px 12px;
    border: 1px solid #000;
    border-radius: 5px;
    font-size: 14px;
    line-height: 1.467;
    box-shadow: inset 0 1px 1px rgba(0,0,0,.075);
    transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
    color: #555;
    background-color: #fff;
    background-image: none;
  }
  
  input[type=text],
  input[type=number] {
    text-align: center;
  }
  input[type=text]:disabled,
  input[type=number]:disabled {
    background: #c1c1c1;
  }
  input::placeholder {
    color: #ccc;
  }
  input[type=text]:disabled::placeholder,
  input[type=number]:disabled::placeholder {
    color: #999999;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }

  
  input[type=number].has-error,
  input[type=number]:active.has-error {
    border: 2px solid rgb(195,13,12);
    outline: none;
  }
  .input-container .error {
    background-color: rgb(195,13,12);
    padding: 10px 30px;
    color: white;
    display: inline-block;
    margin-top: 7px;
    position: relative;
  }
  .input-container .error::before {
    content: "";
    background-color: rgb(195,13,12);
    position: absolute;
    width: 10px;
    height: 10px;
    top: -5px;
    left: 10px;
    z-index: 50;
    transform: rotate(45deg);
  }


  .input-tag {
    font-size: 12px;
    color: #666766;
    cursor: default;
  }
  .tolerance-tag {
    float: right;
    margin-top: 6px;
  }


  .info-tooltip {
    position: relative;
    display: inline-block;
    color: #0099FF;
    // text-decoration: underline;
    border-bottom: 1px dotted #0099FF; /* If you want dots under the hoverable text */
    line-height: 1;
  }
  .info-tooltip .info-tooltiptext {
    visibility: hidden;
    position: absolute;
    z-index: 1;
    width: fit-content;
    bottom: 100%;
    right: -5px;
    margin-bottom: 2px;
    margin-left: -200px;
    padding: 5px 15px;
    background-color: black;
    color: #fff;
    text-align: center;
    font-weight: bold;
  }
  .info-tooltip:hover .info-tooltiptext {
    visibility: visible;
  }
}
</style>
